export interface IntelligenceFrame {
  event: string | null;
  data: string;
  id: string | null;
  retry: number | null;
  comment: string | null;
}

export function isKeepAlive(frame: IntelligenceFrame): boolean {
  return frame.comment !== null && frame.event === null && frame.data === "" && frame.id === null;
}

export class SseFrameScanner {
  private partialLine = "";
  private event: string | null = null;
  private dataLines: string[] = [];
  private id: string | null = null;
  private retry: number | null = null;
  private commentLines: string[] = [];
  private started = false;

  push(chunk: string): IntelligenceFrame[] {
    const frames: IntelligenceFrame[] = [];
    let buf = this.partialLine + chunk;
    this.partialLine = "";

    let heldCr = false;
    if (buf.endsWith("\r")) {
      buf = buf.slice(0, -1);
      heldCr = true;
    }
    const parts = buf.split(/\r\n|\r|\n/);
    const tail = parts.pop() ?? "";
    for (const line of parts) {
      const frame = this.line(line);
      if (frame) frames.push(frame);
    }
    this.partialLine = heldCr ? `${tail}\r` : tail;
    return frames;
  }

  private line(line: string): IntelligenceFrame | null {
    if (line === "") return this.dispatch();
    if (line.startsWith(":")) {
      this.started = true;
      this.commentLines.push(stripOneSpace(line.slice(1)));
      return null;
    }
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? "" : stripOneSpace(line.slice(colon + 1));
    this.started = true;
    switch (field) {
      case "event":
        this.event = value;
        break;
      case "data":
        this.dataLines.push(value);
        break;
      case "id":
        if (!value.includes("\u0000")) this.id = value;
        break;
      case "retry":
        if (/^\d+$/.test(value)) this.retry = Number(value);
        break;
      default:
        break;
    }
    return null;
  }

  private dispatch(): IntelligenceFrame | null {
    if (!this.started) return null;
    const frame: IntelligenceFrame = {
      event: this.event,
      data: this.dataLines.join("\n"),
      id: this.id,
      retry: this.retry,
      comment: this.commentLines.length ? this.commentLines.join("\n") : null,
    };
    this.event = null;
    this.dataLines = [];
    this.id = null;
    this.retry = null;
    this.commentLines = [];
    this.started = false;
    return frame;
  }
}

function stripOneSpace(value: string): string {
  return value.startsWith(" ") ? value.slice(1) : value;
}

export function splitCompleteFrames(buf: Uint8Array): {
  complete: Uint8Array;
  pending: Uint8Array;
} {
  const LF = 0x0a;
  const CR = 0x0d;
  let end = -1;
  let i = 0;
  while (i < buf.length) {
    const b = buf[i];
    if (b !== LF && b !== CR) {
      i += 1;
      continue;
    }
    let len = 0;
    if (b === CR && buf[i + 1] === LF && buf[i + 2] === CR && buf[i + 3] === LF) len = 4;
    else if (b === CR && buf[i + 1] === LF && buf[i + 2] === LF) len = 3;
    else if (b === LF && buf[i + 1] === CR && buf[i + 2] === LF) len = 3;
    else if (b === LF && buf[i + 1] === LF) len = 2;
    else if (b === CR && buf[i + 1] === CR) len = 2;
    if (len === 0) {
      i += 1;
      continue;
    }
    end = i + len;
    i = end;
  }
  if (end < 0) return { complete: buf.subarray(0, 0), pending: buf };
  return { complete: buf.subarray(0, end), pending: buf.subarray(end) };
}

export function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length === 0) return b;
  if (b.length === 0) return a;
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export const RUN_STARTED = "RUN_STARTED";
const RUN_FINISHED = "RUN_FINISHED";
export const RUN_ERROR = "RUN_ERROR";
const BFF_STREAM_ERROR = "error";

export interface IntelligenceStreamHandle {
  runId: string;
  threadId: string | null;
}

export function runStartedHandle(frame: IntelligenceFrame): IntelligenceStreamHandle | null {
  if (frame.event !== RUN_STARTED || frame.data === "") return null;
  let body: Record<string, unknown>;
  try {
    body = JSON.parse(frame.data) as Record<string, unknown>;
  } catch {
    return null;
  }
  const runId = firstString(body.runId, body.run_id);
  if (!runId) return null;
  return { runId, threadId: firstString(body.threadId, body.thread_id) };
}

function firstString(...values: unknown[]): string | null {
  for (const v of values) if (typeof v === "string" && v !== "") return v;
  return null;
}

export type TerminalReason = "finished" | "run-error" | "bff-error";

export function terminalReason(frame: IntelligenceFrame): TerminalReason | null {
  if (frame.event === RUN_FINISHED) return "finished";
  if (frame.event === RUN_ERROR) return "run-error";
  if (frame.event === BFF_STREAM_ERROR) return "bff-error";
  return null;
}

export interface RelayState {
  runId: string | null;
  threadId: string | null;
  lastEventId: string | null;
  terminal: TerminalReason | null;
  frames: number;
  replays: number;
  framesThisHop: number;
  hops: number;
  emptyHops: number;
}

export function newRelayState(
  resume?: { runId?: string | null; lastEventId?: string | null } | null,
): RelayState {
  return {
    runId: resume?.runId ?? null,
    threadId: null,
    lastEventId: resume?.lastEventId ?? null,
    terminal: null,
    frames: 0,
    replays: 0,
    framesThisHop: 0,
    hops: 0,
    emptyHops: 0,
  };
}

export function observeFrame(state: RelayState, frame: IntelligenceFrame): RelayState {
  if (isKeepAlive(frame)) return state;

  const handle = runStartedHandle(frame);
  const newRun = handle !== null && handle.runId !== state.runId;
  if (newRun) state.lastEventId = null;

  if (
    !newRun &&
    frame.id !== null &&
    state.lastEventId !== null &&
    isNotNewerEventId(frame.id, state.lastEventId)
  ) {
    state.replays += 1;
    return state;
  }

  state.frames += 1;
  state.framesThisHop += 1;
  if (frame.id !== null) state.lastEventId = frame.id;
  if (handle) {
    state.runId = handle.runId;
    state.threadId = handle.threadId ?? state.threadId;
  }
  const terminal = terminalReason(frame);
  if (terminal) state.terminal = terminal;
  return state;
}

export function compareEventIds(a: string, b: string): number | null {
  const pa = /^(\d+)\.(\d+)$/.exec(a);
  const pb = /^(\d+)\.(\d+)$/.exec(b);
  if (!pa || !pb) return null;
  const out = Number(pa[1]) - Number(pb[1]);
  return out !== 0 ? out : Number(pa[2]) - Number(pb[2]);
}

function isNotNewerEventId(id: string, watermark: string): boolean {
  const order = compareEventIds(id, watermark);
  return order !== null && order <= 0;
}

export function startedHop(state: RelayState): RelayState {
  state.hops += 1;
  state.framesThisHop = 0;
  return state;
}

export function endedHop(state: RelayState): RelayState {
  if (state.hops > 0) {
    if (state.framesThisHop === 0) state.emptyHops += 1;
    else state.emptyHops = 0;
  }
  return state;
}

export interface ReattachLimits {
  maxHops: number;
  maxEmptyHops: number;
  budgetMs: number;
}

export const DEFAULT_REATTACH_LIMITS: ReattachLimits = {
  maxHops: 12,
  maxEmptyHops: 5,
  budgetMs: 40 * 60 * 1000,
};

export function reattachBackoffMs(emptyHops: number): number {
  return emptyHops <= 0 ? 0 : Math.min(emptyHops * 2000, 10_000);
}

export type ReattachDecision =
  | { kind: "reattach"; runId: string; lastEventId: string | null }
  | { kind: "stop"; reason: RelayStopReason; detail: string };

export type RelayStopReason =
  | "terminal"
  | "no-run-handle"
  | "limit"
  | "client-gone"
  | "reattach-failed";

export function relayAbandonedRun(reason: RelayStopReason): boolean {
  return reason !== "terminal" && reason !== "client-gone";
}

export function planReattach(
  state: RelayState,
  limits: ReattachLimits,
  elapsedMs: number,
): ReattachDecision {
  if (state.terminal) {
    return { kind: "stop", reason: "terminal", detail: state.terminal };
  }
  if (!state.runId) {
    return {
      kind: "stop",
      reason: "no-run-handle",
      detail:
        "the stream ended without a RUN_STARTED frame, so no run id was ever captured — " +
        "re-read the conversation for the turn's final state",
    };
  }
  if (state.hops >= limits.maxHops) {
    return { kind: "stop", reason: "limit", detail: `hop limit ${limits.maxHops} reached` };
  }
  if (elapsedMs >= limits.budgetMs) {
    return {
      kind: "stop",
      reason: "limit",
      detail: `relay budget ${Math.round(limits.budgetMs / 1000)}s exhausted`,
    };
  }
  if (state.emptyHops >= limits.maxEmptyHops) {
    return {
      kind: "stop",
      reason: "limit",
      detail: `${state.emptyHops} consecutive re-attachments delivered no frames`,
    };
  }
  return { kind: "reattach", runId: state.runId, lastEventId: state.lastEventId };
}

export const RELAY_NOTICE_EVENT = "lti-relay";

export interface RelayNotice {
  reason: RelayStopReason;
  detail: string;
  runId: string | null;
  lastEventId: string | null;
  status?: number;
  code?: string | null;
  truncatedTail?: true;
}

export function encodeRelayNotice(notice: RelayNotice): string {
  return `event: ${RELAY_NOTICE_EVENT}\ndata: ${JSON.stringify(notice)}\n\n`;
}

export interface ApprovalDecisionInput {
  toolCallId: string;
  approved: boolean;
  reason?: string | null;
}

export type ToolResultInput =
  | { toolCallId: string; result: string }
  | { toolCallId: string; error: string };

export function approvalDecisionBody(input: ApprovalDecisionInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    tool_call_id: input.toolCallId,
    approved: input.approved,
  };
  if ("reason" in input) body.reason = input.reason;
  return body;
}
